import { COMMENT_PREFIX } from '@/types/liveStream'
import { GENDER } from '@/types/userProfile'

export const USER_TYPE = {
  GENERAL_USER: 1, // 一般ユーザ
  OFFICIAL_USER: 2, // 公認ユーザ
} as const

export const NOTICE_LIMIT = {
  HOME_VIEW: 3,
  NOTICE_LIST_VIEW: 10,
} as const

// 共通で使用するメッセージを定義
export const MESSAGES = {
  fieldRequired: '必須項目です',
  // ここに関数を置くのは不自然な気がするので仮
  invalidStringLength: (min: number, max: number) => `${min}文字以上${max}文字以下で入力してください`,
  containsNgWord: '入力したテキストに不適切な表現が含まれています',
  containsNgWordInFilename: '添付ファイルのファイル名に不適切な表現が含まれています',
  unexpectedError: '時間をおいて再度お試し下さい',
  mustBePositive: '1以上で登録して下さい',
  banned: 'BANされたユーザです。',
} as const

export const LIVE_PRODUCT_REGISTERED = {
  LIMIT: 100, //バリデーションのために必要なので一旦100をリミットに設定
} as const

export const ORDER_LIMIT = {
  LIST_VIEW: 10,
  COMPLETED_MESSAGE_LENGTH: 1000,
} as const

// 依頼一覧（予約）を取得する際の発注・受注の選択
export const ORDER_TYPES = {
  ORDERED: 1, // 発注
  RECEIVED: 2, // 受注
} as const

// 依頼のステータス
export const ORDER_STATUSES = {
  ORDERED: 0,
  RECEIVED: 1,
  COMPLETED: 2,
  CANCELLED: 99,
} as const
// 依頼のステータスラベル
export const ORDER_STATUS_LABELS = {
  [ORDER_STATUSES.ORDERED]: '依頼中',
  [ORDER_STATUSES.RECEIVED]: '受諾済み',
  [ORDER_STATUSES.COMPLETED]: '完了',
  [ORDER_STATUSES.CANCELLED]: 'キャンセル',
} as const

// 請求の支払方法
export const BILLING_PAYMENT_METHODS = {
  CREDIT_CARD: 1,
  CASH: 2,
} as const
// 請求の支払方法ラベル
export const BILLING_PAYMENT_METHOD_LABELS = {
  [ORDER_TYPES.ORDERED]: {
    [BILLING_PAYMENT_METHODS.CREDIT_CARD]: 'クレジットカード',
    [BILLING_PAYMENT_METHODS.CASH]: '現金払い',
  },
  [ORDER_TYPES.RECEIVED]: {
    [BILLING_PAYMENT_METHODS.CREDIT_CARD]: 'クレジットカード',
    [BILLING_PAYMENT_METHODS.CASH]: '現金払い',
  },
} as const

export const USER_SCHEDULE_TYPE = {
  RESERVATION: 1, // 予約
  HOLIDAY: 2, // 休暇
  BUFFER: 3, // 予約のバッファタイム
} as const

export const OFFER_TERMS_PAGE_SIZE = {
  ONE_WEEK: 7,
} as const

export const DAY_OF_WEEKS: string[] = ['(日)', '(月)', '(火)', '(水)', '(木)', '(金)', '(土)']
export const DAY_OF_WEEKS_TEXT: string[] = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日']

export const RESERVATION_INTERVAL: number[] = [90, 60, 30]
export const BUFFER_TIME: number[] = [60, 30, 0]

export const OFFER_COURSE_REGISTERED = {
  LIMIT: 20,
} as const

export const OFFER_AREA_REGISTERED = {
  LIMIT: 20,
} as const

export const NOTIFICATION_TYPE = {
  NOTIFICATION_FROM_ADMIN: 1, // 運営からの通知
  RESERVATION_NOTIFICATION: 2, // 予約通知
  INQUIRY_NOTIFICATION: 3, // お問い合わせ通知
  REVIEW_NOTIFICATION: 4, // 評価通知
  DEPOSIT_NOTIFICATION: 5, // 出金通知
  CHAT_MESSAGE_SEND_OR_UPDATE_NOTIFICATION: 6,
}

export const CHAT_MESSAGE = {
  MAX_FILES: 1,
}

export const SORT_ORDERS = {
  ASC: 1, // 昇順
  DESC: 2, // 降順
}

export const BANK_TYPE = {
  NORMAL: 1,
  CHECKING: 2,
  SAVINGS: 4,
} as const

export type BANK_TYPES = (typeof BANK_TYPE)[keyof typeof BANK_TYPE]

export const BANK_TYPE_STRING = {
  [BANK_TYPE.NORMAL]: '普通',
  [BANK_TYPE.CHECKING]: '当座',
  [BANK_TYPE.SAVINGS]: '貯蓄',
} as const

// 出金関連
export const WITHDRAWAL_LIMIT = {
  LIST_VIEW: 10, // 一覧表示数
} as const

export const WITHDRAWAL_STATUSES = {
  SUCCESSFUL: 1,
  FAILED: 2,
} as const

export const WITHDRAWAL_STATUS_LABELS = {
  [WITHDRAWAL_STATUSES.SUCCESSFUL]: '成功',
  [WITHDRAWAL_STATUSES.FAILED]: '失敗',
} as const

export const WITHDRAWAL_RESULTS = {
  REGISTRATION_COMPLETED: '0',
  DATA_CREATION_COMPLETED: '1',
  DATA_CREATION_FAILED: '2',
  COMPLETED: '3',
  FAILED: '4',
} as const

export const WITHDRAWAL_RESULT_LABELS = {
  [WITHDRAWAL_RESULTS.REGISTRATION_COMPLETED]: '登録完了',
  [WITHDRAWAL_RESULTS.DATA_CREATION_COMPLETED]: '送金データ作成完了',
  [WITHDRAWAL_RESULTS.DATA_CREATION_FAILED]: '送金データ作成失敗',
  [WITHDRAWAL_RESULTS.COMPLETED]: '送金完了',
  [WITHDRAWAL_RESULTS.FAILED]: '送金失敗',
} as const

export const BALANCE_HISTORY_TYPE = {
  DEPOSIT: 1, // 売上入金
  WITHDRAWAL: 2, // 出金
  REFUND: 3, // 返金
}

export const ADMIN_BALANCE_HISTORY_TYPE = {
  DEPOSIT: 1, // 売上入金
  WITHDRAWAL: 2, // 出金
  REFUND: 3, // 返金
}
export const WITHDRAWAL_TABS = {
  REQUEST: 1, // 出金依頼タブ
  HISTORY: 2, // 出金履歴タブ
}

export const BILLING_PAYMENT_STATUSES = {
  INITIAL: 1, // 初期状態
  AUTHORIZED: 2, // 限度額確保
  PROCESSED: 3, // 処理済、依頼者への請求が発生する
  REFUNDED: 99, // 返金
} as const

export const BILLING_PAYMENT_STATUSES_LABELS = {
  [BILLING_PAYMENT_STATUSES.INITIAL]: '未決済',
  [BILLING_PAYMENT_STATUSES.AUTHORIZED]: '決済済み',
  [BILLING_PAYMENT_STATUSES.PROCESSED]: '決済済み',
  [BILLING_PAYMENT_STATUSES.REFUNDED]: '返金済み',
} as const

export const WITHDRAWAL_AMOUNT_ONCE_MAX = 1000000 // 出金1回あたり最大額

export const bankCodeJpPost = '9900'

export const cancelTimeLimit = 60
export const PASSWORD_RULE = {
  MIN_LENGTH: 6, //パスワード最低文字数
  MAX_LENGTH: 16, //パスワード最大文字数
  CAN_USE_CHARACTER: '=+-@_.#!/', //パスワード仕様可能記号
  PATTERN: /^[a-zA-Z\d=+\-@_.#!/]+$/,
}

export const maxBodyLenth = 50000000 // 50MB
export const maxConstLength = 50000000 // 50MB

export const maxUploadFiles = 1
export const maxUploadFileSize = 50000000 // 50MB

export const REGISTER_TYPES = {
  ORDER: 0, //依頼
  RECEIVE: 1, //受注
  RECEIVE_STOP: 2, //受注休止
}

export const SMS_AUTH_TRANSACTION_SOURCE = {
  ORDER: 'order', // 依頼ボタン
  TYPE_SWITCH_REGISTER: 'register', // 受注ユーザ切り替え時(依頼ユーザ登録後すぐ)
  TYPE_SWITCH_PROFILE: 'profile', // 受注ユーザ切り替え時(ユーザプロフィール画面で仕事を受注するボタン押下)
} as const

export const NEWER_PICKUP_USER_RANGE = 30 * 24 * 60 * 60 // 30日
export const NEWER_USER_PROFILE_RANGE = 30 * 24 * 60 * 60 // 30日

export const REPORT_STATUSES = {
  INITIAL: 1, //初期状態
  REPORTED: 2, //対応済み
} as const

export const GENDER_LABELS = {
  [GENDER.NO_ANSWER]: '無回答',
  [GENDER.MALE]: '男性',
  [GENDER.FEMALE]: '女性',
  [GENDER.OTHER]: 'その他',
} as const

// 検索ラジオボタンステータス
export const SEARCH_RADIO_STATUSES = {
  LIVE: 0,
  ORDER: 1,
  BOTH: 2,
} as const
// 検索ラジオボタンステータスラベル
export const SEARCH_RADIO_STATUSES_LABELS = {
  [SEARCH_RADIO_STATUSES.LIVE]: 'LIVE中',
  [SEARCH_RADIO_STATUSES.ORDER]: 'お仕事受付中',
  [SEARCH_RADIO_STATUSES.BOTH]: '両方',
} as const

export const COMMENT_SEPARATOR = '>>>'
export const BASE_COMMENT_STYLE_CLASS = 'comment-body'

// 配信コメントのテンプレート
export const COMMENT_TEMPLATE = {
  [COMMENT_PREFIX.HOST_COMMENT]: ({ comment }: { comment: string }) => `${comment}`,
  [COMMENT_PREFIX.AUDIENCE_COMMENT]: ({ comment }: { comment: string }) => `${comment}`,
  [COMMENT_PREFIX.SYSTEM]: ({ comment }: { comment: string }) => `${comment}`,
  [COMMENT_PREFIX.JOIN]: () => `が入室しました`,
  [COMMENT_PREFIX.LEAVE]: () => `が退出しました`,
  [COMMENT_PREFIX.PURCHASED]: ({ productName }: { productName: string }) => `${productName}を購入しました`,
  [COMMENT_PREFIX.BLOCK]: () => `が配信者によってブロックされました`,
  [COMMENT_PREFIX.RELEASE_BLOCK]: () => `のブロックが解除されました`,
  [COMMENT_PREFIX.MUTE]: () => `が配信者によってミュートされました`,
  [COMMENT_PREFIX.RELEASE_MUTE]: () => `のミュートが解除されました`,
  [COMMENT_PREFIX.NAGESEN]: ({ amount }: { amount: string }) => `が${amount}円をホストに投げ銭しました`,
  [COMMENT_PREFIX.CAMERA_MUTED]: () => 'ホストのカメラがOFFになりました',
  [COMMENT_PREFIX.CAMERA_UNMUTED]: () => 'ホストのカメラがONになりました',
  [COMMENT_PREFIX.MIC_MUTED]: () => 'ホストのマイクがOFFになりました',
  [COMMENT_PREFIX.MIC_UNMUTED]: () => 'ホストのマイクのONになりました',
  [COMMENT_PREFIX.UPDATE_PRODUCTS]: () => '商品一覧を更新してください',
  [COMMENT_PREFIX.JOIN_FOR_PURCHASE]: () => `の購入手続きが完了しました`,
  [COMMENT_PREFIX.LEAVE_FOR_PURCHASE]: () => `が購入手続きに入りました`,
  [COMMENT_PREFIX.JOIN_FOR_PURCHASE_FAILED]: () => `の購入手続きに失敗・キャンセルしました`,
} as const

// 配信コメントのクラス名
export const COMMENT_STYLE_CLASS = {
  [COMMENT_PREFIX.HOST_COMMENT]: 'comment-host-comment',
  [COMMENT_PREFIX.AUDIENCE_COMMENT]: 'comment-audience-comment',
  [COMMENT_PREFIX.SYSTEM]: 'comment-system',
  [COMMENT_PREFIX.JOIN]: 'comment-join',
  [COMMENT_PREFIX.LEAVE]: 'comment-leave',
  [COMMENT_PREFIX.PURCHASED]: 'comment-purchased',
  [COMMENT_PREFIX.BLOCK]: 'comment-block',
  [COMMENT_PREFIX.RELEASE_BLOCK]: 'comment-release-block',
  [COMMENT_PREFIX.MUTE]: 'comment-mute',
  [COMMENT_PREFIX.RELEASE_MUTE]: 'comment-release-mute',
  [COMMENT_PREFIX.NAGESEN]: 'comment-nagesen',
  [COMMENT_PREFIX.CAMERA_MUTED]: 'comment-camera-muted',
  [COMMENT_PREFIX.CAMERA_UNMUTED]: 'comment-camera-unmuted',
  [COMMENT_PREFIX.MIC_MUTED]: 'comment-mic-muted',
  [COMMENT_PREFIX.MIC_UNMUTED]: 'comment-mic-unmuted',
  [COMMENT_PREFIX.UPDATE_PRODUCTS]: 'comment-update-products',
  [COMMENT_PREFIX.JOIN_FOR_PURCHASE]: 'comment-join-for-purchase',
  [COMMENT_PREFIX.LEAVE_FOR_PURCHASE]: 'comment-leave-for-purchase',
  [COMMENT_PREFIX.JOIN_FOR_PURCHASE_FAILED]: 'comment-join-for-purchase-failed',
} as const

// 配信コメントのアイコンURL(ユーザーはパラメータから来るURL優先)
export const COMMENT_ICON_URL = {
  [COMMENT_PREFIX.HOST_COMMENT]: require('@/assets/img/ico-livestream-host.svg'),
  [COMMENT_PREFIX.AUDIENCE_COMMENT]: require('@/assets/img/ico-livestream-audience.svg'),
  [COMMENT_PREFIX.SYSTEM]: require('@/assets/img/ico-livestream-system.svg'),
  [COMMENT_PREFIX.JOIN]: require('@/assets/img/ico-livestream-join.png'),
  [COMMENT_PREFIX.LEAVE]: require('@/assets/img/ico-livestream-leave.png'),
  [COMMENT_PREFIX.PURCHASED]: require('@/assets/img/ico-livestream-join-for-purchase.png'),
  [COMMENT_PREFIX.BLOCK]: require('@/assets/img/ico-livestream-block.png'),
  [COMMENT_PREFIX.RELEASE_BLOCK]: require('@/assets/img/ico-livestream-release-blocked.png'),
  [COMMENT_PREFIX.MUTE]: require('@/assets/img/ico-livestream-mute.png'),
  [COMMENT_PREFIX.RELEASE_MUTE]: require('@/assets/img/ico-livestream-release-muted.png'),
  [COMMENT_PREFIX.NAGESEN]: require('@/assets/img/ico-livestream-join-for-purchase.png'),
  [COMMENT_PREFIX.CAMERA_MUTED]: require('@/assets/img/ico-video-on.svg'),
  [COMMENT_PREFIX.CAMERA_UNMUTED]: require('@/assets/img/ico-video-off.svg'),
  [COMMENT_PREFIX.MIC_MUTED]: require('@/assets/img/ico-mic-off.svg'),
  [COMMENT_PREFIX.MIC_UNMUTED]: require('@/assets/img/ico-mic-on.svg'),
  [COMMENT_PREFIX.UPDATE_PRODUCTS]: require('@/assets/img/ico-livestream-join-for-purchase.png'),
  [COMMENT_PREFIX.JOIN_FOR_PURCHASE]: require('@/assets/img/ico-livestream-join-for-purchase.png'),
  [COMMENT_PREFIX.LEAVE_FOR_PURCHASE]: require('@/assets/img/ico-livestream-leave-for-purchase.png'),
  [COMMENT_PREFIX.JOIN_FOR_PURCHASE_FAILED]: require('@/assets/img/ico-livestream-join-for-purchase-failed.png'),
} as const

// 出金関連
export const LIVE_AUDIENCES_LIMIT = {
  LIST_VIEW: 1, // 一覧表示数
} as const

// 投げ銭時アニメーション
export const SOCIAL_TIPPING_THNAKS_ANIMATION = {
  IMG_URL: require('@/assets/video/flowerPOP-SAKURA.webp'),
}
